import { useState } from 'react';
import { DialogContent, DialogActions, Stack, TextField, Button } from '@mui/material';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from '../NewComponent/styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import {
  GetInspectionPlansDocument,
  InputMaybe,
  TemplateFiltersInput,
  useCreateInspectionPlanMutation,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { Permissions } from '@/types';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedTemplateFilters?: InputMaybe<TemplateFiltersInput>[];
  selectedDeckIds?: string[] | null;
  selectedWorkpackIds?: string[] | null;
  selectedPaintRegionIds?: string[] | null;
};

export const SaveViewModal = ({
  open,
  onClose,
  selectedTemplateFilters,
  selectedDeckIds,
  selectedWorkpackIds,
  selectedPaintRegionIds,
}: Props) => {
  const [inspectionPlanName, setInspectionPlanName] = useState('');
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();
  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS);

  const [createInspectionPlan] = useCreateInspectionPlanMutation({
    refetchQueries: [GetInspectionPlansDocument],
  });

  const handleSave = async () => {
    // allow if user is admin OR has the manage inspection plans permission
    if (!isAdmin && !canManageInspectionPlans) {
      showSnackBar('user does not have permission to save view or plan', 'error');
      return;
    }

    try {
      const filters = {
        templateFilters: selectedTemplateFilters,
        deckIds: selectedDeckIds ?? [],
        workpackIds: selectedWorkpackIds ?? [],
        paintRegionIds: selectedPaintRegionIds ?? [],
      };

      await createInspectionPlan({
        variables: { input: { structureId, name: inspectionPlanName, filters } },
      });
      showSnackBar('View saved', 'success');
      onClose();
      setInspectionPlanName('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      showSnackBar(message, 'error');
    }
  };

  return (
    <GenericModal
      open={open}
      handleClose={onClose}
      title="Save view"
      description={
        'This will store your current layout and settings for quick access later. Give this view a name so you can easily recognize it in your saved views.'
      }
      sx={{ ...dialogOverlay, height: 'fit-content' }}
    >
      <DialogContent sx={{ p: 3, minWidth: '400px' }}>
        <Stack spacing={2}>
          <TextField
            variant="standard"
            label="View name"
            value={inspectionPlanName}
            onChange={(e) => setInspectionPlanName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!inspectionPlanName.trim() || !(canManageInspectionPlans || isAdmin)}
        >
          Confirm
        </Button>
      </DialogActions>
    </GenericModal>
  );
};
